import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Home, Search, Clock, ListFilter, Trash2, CheckCircle2, Circle, CalendarCheck, Phone, Plus, User, Loader2, Link2, Info, X, Lock, Shield, UserCheck, Globe, Edit, HandHelping, Users, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { API, norm, formatPhone } from '@/lib/helpers';
import { formatBrazilDateTimeCompact } from '@/lib/timezone';

const FILTROS = [
  { key: 'todas', label: 'Todas', color: 'bg-indigo-100 text-indigo-600' },
  { key: 'pendente', label: 'Pendentes', color: 'bg-amber-100 text-amber-600' },
  { key: 'agendada', label: 'Agendadas', color: 'bg-blue-100 text-blue-600' },
  { key: 'realizada', label: 'Realizadas', color: 'bg-green-100 text-green-600' },
];

const VISIBILIDADE_OPTIONS = [
  { value: 'privado', label: 'Privado', icon: Shield, desc: 'Apenas para você' },
  { value: 'auxiliares', label: 'Auxiliares', icon: UserCheck, desc: 'Visível para auxiliares' },
  { value: 'publico', label: 'Público', icon: Globe, desc: 'Agenda pública (em breve)' },
];

const STATUS_LABELS = { pendente: 'Pendente', agendada: 'Agendada', realizada: 'Realizada' };
const STATUS_COLORS = { pendente: 'bg-amber-100 text-amber-700', agendada: 'bg-blue-100 text-blue-700', realizada: 'bg-green-100 text-green-700' };
const STATUS_ICONS = { pendente: Circle, agendada: CalendarCheck, realizada: CheckCircle2 };
const BACKEND_URL = window.location.origin;

function photoFrameStyle(person = {}) {
  return { objectPosition: `${Number(person.foto_pos_x ?? 50)}% ${Number(person.foto_pos_y ?? 50)}%`, transform: `scale(${Number(person.foto_zoom ?? 1)})`, transformOrigin: `${Number(person.foto_pos_x ?? 50)}% ${Number(person.foto_pos_y ?? 50)}%` };
}

function visitPeople(visit = {}) {
  const people = Array.isArray(visit.pessoas) && visit.pessoas.length ? visit.pessoas : (visit.pessoas_nomes || []).map(nome => ({ nome_completo: nome }));
  return people.filter(person => person?.nome_completo);
}

function peopleFullNames(people = [], fallback = '') {
  const names = people.map(person => person.nome_completo).filter(Boolean);
  if (!names.length) return fallback || 'Solicitação sem nome';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

function VisitAvatar({ visit, testId }) {
  const people = visitPeople(visit);
  const firstWithPhoto = people.find(person => person.foto_thumb_url || person.foto_url) || people[0];
  const photoUrl = firstWithPhoto?.foto_thumb_url || firstWithPhoto?.foto_url;
  if (photoUrl) return <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 overflow-hidden rounded-full border-2 border-white bg-slate-100 shadow-sm sm:h-11 sm:w-11" data-testid={testId}><img src={`${BACKEND_URL}${photoUrl}`} alt={`Foto de ${firstWithPhoto.nome_completo || 'pessoa'}`} className="h-full w-full object-cover" style={photoFrameStyle(firstWithPhoto)} loading="lazy" decoding="async" /></span>;
  return <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 mt-0.5 ${visit.status === 'realizada' ? 'bg-gradient-to-br from-green-500 to-green-600' : visit.status === 'agendada' ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-amber-400 to-amber-500'}`} data-testid={`${testId}-fallback`}><HandHelping className="w-4 h-4 sm:w-5 sm:h-5" /></div>;
}

function yesNo(value) {
  return value ? 'Sim' : 'Não';
}

function exportTimestamp() {
  const now = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

function firstVisitPerson(visit = {}) {
  const people = visitPeople(visit);
  return people[0] || {};
}

async function exportVisitsToExcel(visits = []) {
  const XLSX = await import('xlsx');
  const rows = visits.map(visit => {
    const person = firstVisitPerson(visit);
    const allNames = peopleFullNames(visitPeople(visit), visit.nome);
    return {
      'Status da solicitação': STATUS_LABELS[visit.status] || visit.status || '',
      'Criada em': formatBrazilDateTimeCompact(visit.created_at, ''),
      'Nome(s) envolvidos na visita': allNames,
      'Qtd. pessoas vinculadas': visitPeople(visit).length || '',
      'Nome': person.nome_completo || visit.nome || '',
      'Apelido': person.apelido || '',
      'Gênero': person.genero || '',
      'Data de nascimento': person.data_nascimento || '',
      'Idade': person.idade ?? '',
      'Telefone': person.whatsapp || visit.telefone || '',
      'E-mail': person.email || '',
      'Endereço': person.endereco_formatado || person.endereco || visit.endereco || '',
      'Logradouro': person.logradouro || '',
      'Número': person.numero || '',
      'Bairro': person.bairro || '',
      'Cidade': person.cidade || '',
      'UF': person.uf || '',
      'CEP': person.cep || '',
      'Complemento': person.complemento || '',
      'Nome da mãe': person.nome_mae || '',
      'Telefone da mãe': person.whatsapp_mae || '',
      'Nome do pai': person.nome_pai || '',
      'Telefone do pai': person.whatsapp_pai || '',
      'Batizado': person.pessoa_id ? yesNo(person.batizado) : '',
      'Selado': person.pessoa_id ? yesNo(person.selado) : '',
      'Auxiliar de jovens': person.pessoa_id ? yesNo(person.auxiliar_jovens) : '',
      'Grupo de visita': person.pessoa_id ? yesNo(person.grupo_visita) : '',
      'Organista': person.pessoa_id ? yesNo(person.organista) : '',
      'Instrutora musicalização': person.pessoa_id ? yesNo(person.instrutora_musicalizacao) : '',
      'Integrante EBI': person.pessoa_id ? yesNo(person.integrante_ebi) : '',
      'Músico': person.pessoa_id ? yesNo(person.musico) : '',
      'Instrumentos': (person.instrumentos || []).join(', '),
      'Instrutor GEM': person.pessoa_id ? yesNo(person.instrutor_gem) : '',
      'Aluno GEM': person.pessoa_id ? yesNo(person.aluno_gem) : '',
      'Fila': person.fila || '',
      'Engajamento': person.engajamento_nivel || '',
      'Observação da solicitação': visit.observacao || '',
      'ID da solicitação': visit.visita_id || '',
      'ID da pessoa exportada': person.pessoa_id || '',
    };
  });
  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = Object.keys(rows[0] || { 'Status da solicitação': '' }).map(key => ({ wch: Math.min(Math.max(key.length + 4, 14), 34) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Solicitações');
  XLSX.writeFile(workbook, `solicitacoes-visitas-${exportTimestamp()}.xlsx`);
}

export default function VisitasTab({ visitas, onRefresh, tiposEvento = [], pessoas = [] }) {
  const [filter, setFilter] = useState('todas');
  const [search, setSearch] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ nome: '', telefone: '', observacao: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [matchedPessoa, setMatchedPessoa] = useState(null);
  const [phoneChecked, setPhoneChecked] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const debounceRef = useRef(null);

  // Event creation from visit
  const [eventOpen, setEventOpen] = useState(false);
  const [eventForm, setEventForm] = useState({ name: '', date: '', tipo_evento_id: '', pessoa_id: '', pessoa_nome: '', pessoa_ids: [], pessoas_nomes: [], visita_id: '', visibilidade: 'auxiliares', publico: false });
  const [eventSaving, setEventSaving] = useState(false);
  const [eventPessoaSearch, setEventPessoaSearch] = useState('');
  const [publicoConfirm, setPublicoConfirm] = useState(false);
  const [eventPessoaDropdown, setEventPessoaDropdown] = useState(false);

  const [editingEventId, setEditingEventId] = useState(null);

  const handleExportVisits = async () => {
    if (!filtered.length) { toast.error('Não há solicitações para exportar.'); return; }
    try {
      await exportVisitsToExcel(filtered);
      toast.success('Excel de solicitações gerado.');
    } catch (error) {
      console.error('Erro ao exportar solicitações de visitas', error);
      toast.error('Erro ao exportar Excel.');
    }
  };

  const openEventFromVisita = async (v) => {
    if (!tiposEvento.some(t => t.permite_pessoa)) {
      toast.error('Nenhum tipo de evento com "Permite associar pessoa" ativo. Configure em Configurações > Tipos de Evento.');
      return;
    }
    const visitaTipo = tiposEvento.find(t => t.permite_pessoa && norm(t.nome).includes('visita')) || tiposEvento.find(t => t.permite_pessoa);
    if (v.status === 'agendada') {
      try {
        const r = await fetch(`${API}/events/by-visita/${v.visita_id}`, { credentials: 'include' });
        if (r.ok) {
          const ev = await r.json();
          if (ev && ev.event_id) {
            setEditingEventId(ev.event_id);
            setEventForm({
              name: ev.name || '',
              date: ev.date || '',
              tipo_evento_id: ev.tipo_evento_id || '',
              pessoa_id: ev.pessoa_id || '',
              pessoa_nome: ev.pessoa_nome || '',
              pessoa_ids: ev.pessoa_ids || [],
              pessoas_nomes: ev.pessoas_nomes || [],
              visita_id: v.visita_id,
              visibilidade: ev.visibilidade || 'auxiliares',
              publico: ev.publico || false,
            });
            setEventPessoaSearch('');
            setEventOpen(true);
            return;
          }
        }
      } catch (error) {
        console.warn('Não foi possível reaproveitar evento vinculado à visita.', error);
      }
    }
    setEditingEventId(null);
    setEventForm({
      name: (() => {
        const nomes = (v.pessoas_nomes || []).map(n => n.split(' ')[0]);
        if (nomes.length === 0) return v.nome;
        if (nomes.length === 1) return nomes[0];
        return nomes.slice(0, -1).join(', ') + ' e ' + nomes[nomes.length - 1];
      })(),
      date: '',
      tipo_evento_id: visitaTipo?.tipo_evento_id || '',
      pessoa_id: v.pessoa_ids?.[0] || '',
      pessoa_nome: v.pessoas_nomes?.[0] || '',
      pessoa_ids: v.pessoa_ids || [],
      pessoas_nomes: v.pessoas_nomes || [],
      visita_id: v.visita_id,
      visibilidade: 'auxiliares',
      publico: false,
    });
    setEventPessoaSearch('');
    setEventOpen(true);
  };

  const handleCreateEvent = async () => {
    if (!eventForm.name.trim() || !eventForm.date) { toast.error('Nome e data são obrigatórios'); return; }
    setEventSaving(true);
    try {
      const url = editingEventId ? `${API}/events/${editingEventId}` : `${API}/events`;
      const method = editingEventId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(eventForm) });
      if (!r.ok) throw new Error('Erro');
      toast.success(editingEventId ? 'Evento atualizado' : 'Evento criado e visita agendada');
      setEventOpen(false);
      if (!editingEventId) {
        const visita = (visitas || []).find(vi => vi.visita_id === eventForm.visita_id);
        if (visita) await handleStatus(visita, 'agendada');
      }
      onRefresh();
    } catch { toast.error('Erro ao salvar evento'); }
    finally { setEventSaving(false); }
  };

  const selectedEventTipo = tiposEvento.find(t => t.tipo_evento_id === eventForm.tipo_evento_id);

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchDropdown, setSearchDropdown] = useState(false);

  const isPhoneInput = (val) => val.replace(/\D/g, '').length >= 6 || /^\(?\d/.test(val.trim());

  const checkPessoa = useCallback(async (phone) => {
    const clean = phone.replace(/\D/g, '');
    if (clean.length < 10) { setMatchedPessoa(null); setPhoneChecked(false); return; }
    setPhoneChecking(true);
    try {
      const r = await fetch(`${API}/visitas/check-pessoa?telefone=${encodeURIComponent(phone)}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        if (data.found) {
          setMatchedPessoa(data);
          setCreateForm(prev => ({ ...prev, telefone: phone, nome: data.nome_completo }));
        } else {
          setMatchedPessoa(null);
        }
        setPhoneChecked(true);
      } else { setMatchedPessoa(null); setPhoneChecked(true); }
    } catch { setMatchedPessoa(null); setPhoneChecked(true); }
    finally { setPhoneChecking(false); }
  }, []);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    setMatchedPessoa(null);
    setPhoneChecked(false);
    setCreateForm(prev => ({ ...prev, nome: '', telefone: '' }));
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (isPhoneInput(val)) {
      const formatted = formatPhone(val);
      setSearchInput(formatted);
      setCreateForm(prev => ({ ...prev, telefone: formatted }));
      setSearchResults([]);
      setSearchDropdown(false);
      debounceRef.current = setTimeout(() => checkPessoa(formatted), 400);
    } else {
      if (val.trim().length >= 2) {
        const results = pessoas.filter(p => norm(p.nome_completo).includes(norm(val))).slice(0, 8);
        setSearchResults(results);
        setSearchDropdown(results.length > 0);
      } else {
        setSearchResults([]);
        setSearchDropdown(false);
      }
    }
  };

  const selectPessoaFromSearch = (p) => {
    const phone = p.whatsapp || p.whatsapp_pai || p.whatsapp_mae || '';
    setSearchInput(p.nome_completo);
    setSearchDropdown(false);
    setSearchResults([]);
    if (phone) {
      setCreateForm(prev => ({ ...prev, telefone: phone }));
      checkPessoa(phone);
    } else {
      // Person without phone — associate directly
      setMatchedPessoa({ found: true, pessoas: [{ pessoa_id: p.pessoa_id, nome_completo: p.nome_completo, fila: p.fila || '', idade: p.idade != null ? p.idade : null, via: 'Próprio' }] });
      setCreateForm(prev => ({ ...prev, nome: p.nome_completo, telefone: '' }));
      setPhoneChecked(true);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({ nome: '', telefone: '', observacao: '' });
    setSearchInput('');
    setMatchedPessoa(null);
    setPhoneChecked(false);
    setPhoneChecking(false);
    setSearchResults([]);
    setSearchDropdown(false);
  };

  const counts = {
    todas: (visitas || []).length,
    pendente: (visitas || []).filter(v => v.status === 'pendente').length,
    agendada: (visitas || []).filter(v => v.status === 'agendada').length,
    realizada: (visitas || []).filter(v => v.status === 'realizada').length,
  };

  const filtered = (visitas || []).filter(v => {
    if (filter !== 'todas' && v.status !== filter) return false;
    const fullNames = peopleFullNames(visitPeople(v), v.nome);
    if (search && !norm(v.nome).includes(norm(search)) && !norm(fullNames).includes(norm(search)) && !norm(v.telefone).includes(norm(search))) return false;
    return true;
  });

  const handleStatus = async (v, newStatus) => {
    try {
      const r = await fetch(`${API}/visitas/${v.visita_id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ status: newStatus }) });
      if (!r.ok) throw new Error('Erro');
      toast.success(`Status alterado para ${STATUS_LABELS[newStatus]}`);
      onRefresh();
      if (detailItem?.visita_id === v.visita_id) setDetailItem({ ...v, status: newStatus });
    } catch { toast.error('Erro ao alterar status'); }
  };

  const handleDelete = async (v) => {
    try {
      // Delete linked event if exists
      await fetch(`${API}/events/by-visita/${v.visita_id}`, { method: 'DELETE', credentials: 'include' });
      const r = await fetch(`${API}/visitas/${v.visita_id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('Erro');
      toast.success('Visita excluída'); onRefresh(); setDetailItem(null); setDeleteConfirm(null);
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const pessoa_ids = matchedPessoa?.pessoas ? matchedPessoa.pessoas.map(p => p.pessoa_id) : [];
    const pessoas_nomes = matchedPessoa?.pessoas ? matchedPessoa.pessoas.map(p => p.nome_completo) : [];
    let nome;
    if (pessoas_nomes.length > 0) {
      const primeiros = pessoas_nomes.map(n => n.split(' ')[0]);
      nome = primeiros.length === 1 ? primeiros[0] : primeiros.slice(0, -1).join(', ') + ' e ' + primeiros[primeiros.length - 1];
    } else {
      nome = createForm.nome.trim();
    }
    if (!nome) { toast.error('Nome é obrigatório'); return; }
    setCreateLoading(true);
    try {
      const r = await fetch(`${API}/visitas`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ ...createForm, nome, pessoa_ids, pessoas_nomes }) });
      if (!r.ok) throw new Error('Erro');
      toast.success('Visita cadastrada'); setCreateOpen(false); resetCreateForm(); onRefresh();
    } catch { toast.error('Erro ao cadastrar'); }
    finally { setCreateLoading(false); }
  };

  const fmtDate = (d) => {
    return formatBrazilDateTimeCompact(d, d || '');
  };

  const nextStatus = (current) => {
    if (current === 'pendente') return 'agendada';
    if (current === 'agendada') return 'realizada';
    return 'pendente';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Home className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Solic. Visitas</h2>
                  <Badge className="bg-indigo-100 text-indigo-700 text-sm font-bold">{counts.todas}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="visitas-menu">
              {FILTROS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filtro-visita-${f.key}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.color.split(' ')[0]}`}>
                      <ListFilter className={`w-4 h-4 ${f.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-indigo-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={() => setCreateOpen(true)} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="btn-nova-visita">
                <Plus className="w-4 h-4 mr-1" />Nova Solicitação
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Home className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS.find(f => f.key === filter)?.label}</h2>
                  <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'solicitação' : 'solicitações'}</p>
                </div>
              </div>
              <Button size="icon" variant="outline" onClick={handleExportVisits} disabled={!filtered.length} data-testid="visitas-export-excel-button" title="Exportar solicitações para Excel" className="h-9 w-9 flex-shrink-0"><Download className="h-4 w-4" /></Button>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="visitas-search" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="visitas-list">
              {filtered.map(v => {
                const people = visitPeople(v);
                const displayNames = peopleFullNames(people, v.nome);
                return (
                  <div key={v.visita_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`visita-${v.visita_id}`}>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <VisitAvatar visit={v} testId={`visita-avatar-${v.visita_id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                          <span className="truncate max-w-[220px] sm:max-w-none" data-testid={`visita-nomes-${v.visita_id}`}>{displayNames}</span>
                          {v.observacao && (
                            <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-xs px-1.5 cursor-default"><FileText className="w-3 h-3" /></Badge></TooltipTrigger><TooltipContent className="max-w-xs"><p className="whitespace-pre-wrap text-xs">{v.observacao}</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {people.length > 0 && (
                            <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Link2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-testid={`visita-linked-people-${v.visita_id}`} /></TooltipTrigger><TooltipContent><p>Vinculada a {displayNames}</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3 h-3" />{fmtDate(v.created_at)}</span>
                          <span className="flex items-center gap-1 text-xs text-slate-500"><Phone className="w-3 h-3" />{v.telefone}</span>
                          <Badge className={`text-[10px] sm:text-xs ${STATUS_COLORS[v.status]}`}>{STATUS_LABELS[v.status]}{v.status === 'agendada' && v.evento_data ? ` ${new Date(v.evento_data).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' })} ${new Date(v.evento_data).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}` : ''}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-0.5 items-center flex-shrink-0">
                        {v.status === 'agendada' && <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-slate-600 hover:bg-slate-50" onClick={() => openEventFromVisita(v)} data-testid={`editar-evento-${v.visita_id}`} title="Editar evento"><Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></Button>}
                        {v.status !== 'agendada' && <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEventFromVisita(v)} data-testid={`agendar-visita-${v.visita_id}`} title="Agendar"><CalendarCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></Button>}
                        <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(v)} data-testid={`delete-visita-${v.visita_id}`} title="Excluir"><Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Home className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma solicitação de visita {filter !== 'todas' ? STATUS_LABELS[filter]?.toLowerCase() : ''}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!detailItem} onOpenChange={(o) => { if (!o) setDetailItem(null); }}>
        <DialogContent className="max-w-md" data-testid="visita-detail-modal">
          <DialogHeader><DialogTitle>Detalhes da Solicitação</DialogTitle></DialogHeader>
          {detailItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {detailItem.nome?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{detailItem.nome}</p>
                  <Badge className={`text-xs mt-0.5 ${STATUS_COLORS[detailItem.status]}`}>{STATUS_LABELS[detailItem.status]}</Badge>
                </div>
                {detailItem.pessoa_associada && <Link2 className="w-4 h-4 text-blue-500 flex-shrink-0" />}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-slate-400 text-xs">WhatsApp</p><p className="text-slate-900">{detailItem.telefone}</p></div>
                <div><p className="text-slate-400 text-xs">Data</p><p className="text-slate-900">{fmtDate(detailItem.created_at)}</p></div>
                {detailItem.pessoa_associada && <div className="col-span-2"><p className="text-slate-400 text-xs">Pessoa vinculada</p><p className="text-slate-900">{detailItem.pessoa_associada}</p></div>}
                {detailItem.observacao && <div className="col-span-2"><p className="text-slate-400 text-xs">Descrição</p><p className="text-slate-900 mt-1 bg-slate-50 p-2 rounded">{detailItem.observacao}</p></div>}
              </div>
              <div className="flex gap-2 pt-2 border-t">
                {detailItem.status !== 'pendente' && <Button variant="outline" size="sm" onClick={() => handleStatus(detailItem, 'pendente')} data-testid="status-pendente">Pendente</Button>}
                {detailItem.status !== 'agendada' && <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => { setDetailItem(null); openEventFromVisita(detailItem); }} data-testid="status-agendada">Agendar</Button>}
                {detailItem.status !== 'realizada' && <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleStatus(detailItem, 'realizada')} data-testid="status-realizada">Realizada</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) { resetCreateForm(); } setCreateOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="visita-create-modal">
          <DialogHeader><DialogTitle>Nova Solicitação de Visita</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="relative">
              <Label className="text-sm">Pesquise pelo nome ou WhatsApp</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={searchInput} onChange={e => handleSearchChange(e.target.value)} onFocus={() => { if (searchResults.length > 0) setSearchDropdown(true); }} onBlur={() => setTimeout(() => setSearchDropdown(false), 200)} className="pl-9" data-testid="create-visita-search" autoFocus placeholder="Digite um nome ou número..." />
              </div>
              {searchDropdown && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="search-dropdown">
                  {searchResults.map(p => (
                    <button key={p.pessoa_id} type="button" className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2.5 text-sm border-b border-slate-50 last:border-b-0"
                      data-testid={`search-result-${p.pessoa_id}`}
                      onClick={() => selectPessoaFromSearch(p)}>
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[10px] flex-shrink-0">
                        {p.nome_completo?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.nome_completo}</p>
                        {p.fila && <span className="text-xs text-slate-500">{p.fila}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {phoneChecking && (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-1" data-testid="phone-checking">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Buscando pessoa...</span>
              </div>
            )}
            {phoneChecked && matchedPessoa && (
              <div className="mt-3" data-testid="pessoa-cards">
                {(matchedPessoa.pessoas || [matchedPessoa]).map((p, i) => (
                  <div key={i} className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 flex items-center gap-3 mb-3" data-testid={`pessoa-card-${i}`}>
                    <div className="w-11 h-11 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                      {p.nome_completo?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{p.nome_completo}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {p.fila && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">{p.fila}</Badge>}
                        {p.idade != null && <span className="text-xs text-slate-500">{p.idade} anos</span>}
                      </div>
                      {p.via && (
                        <p className="text-[11px] text-slate-500 mt-1">{p.via === 'Relacionada' ? 'Pessoa relacionada' : `Este é o WhatsApp ${p.via === 'Próprio' ? 'da própria pessoa' : p.via === 'Mãe' ? 'da Mãe' : 'do Pai'}`}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!matchedPessoa && !phoneChecking && searchInput.trim().length >= 2 && !searchDropdown && !isPhoneInput(searchInput) && searchResults.length === 0 && (
              <>
                <div>
                  <Label className="text-sm">Nome *</Label>
                  <Input value={createForm.nome} onChange={e => setCreateForm({ ...createForm, nome: e.target.value })} data-testid="create-visita-nome" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">WhatsApp</Label>
                  <Input value={createForm.telefone} onChange={e => setCreateForm({ ...createForm, telefone: formatPhone(e.target.value) })} data-testid="create-visita-telefone" className="mt-1" />
                </div>
              </>
            )}
            {!matchedPessoa && !phoneChecking && isPhoneInput(searchInput) && phoneChecked && (
              <>
                <div>
                  <Label className="text-sm">Nome *</Label>
                  <Input value={createForm.nome} onChange={e => setCreateForm({ ...createForm, nome: e.target.value })} data-testid="create-visita-nome" className="mt-1" />
                </div>
              </>
            )}
            <div>
              <Label className="text-sm">Descrição</Label>
              <Textarea value={createForm.observacao} onChange={e => setCreateForm({ ...createForm, observacao: e.target.value })} rows={3} maxLength={2000} data-testid="create-visita-obs" className="mt-1 resize-none" />
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={createLoading || phoneChecking} data-testid="submit-create-visita">
              {createLoading ? 'Salvando...' : 'Cadastrar Solicitação'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="visita-delete-modal">
          <DialogHeader><DialogTitle>Confirmar Exclusão</DialogTitle></DialogHeader>
          {deleteConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Tem certeza que deseja excluir a solicitação de <span className="font-semibold text-slate-900">{deleteConfirm.nome}</span>?</p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete">Cancelar</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete">Excluir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={eventOpen} onOpenChange={(o) => { if (!o) setEventOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="event-from-visita-modal">
          <DialogHeader><DialogTitle>{editingEventId ? 'Editar Evento' : 'Agendar Evento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-800 mb-2 block">Tipo de Evento</Label>
              <div className={`grid gap-2 ${tiposEvento.filter(t => t.permite_pessoa).length <= 3 ? `grid-cols-${tiposEvento.filter(t => t.permite_pessoa).length}` : 'grid-cols-3'}`}>
                {tiposEvento.filter(t => t.permite_pessoa).map(t => {
                  const active = eventForm.tipo_evento_id === t.tipo_evento_id;
                  return (
                    <button key={t.tipo_evento_id} type="button" onClick={() => setEventForm({ ...eventForm, tipo_evento_id: t.tipo_evento_id })}
                      className={`py-1.5 px-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 border-slate-200 bg-white hover:border-slate-300`}
                      style={active ? { backgroundColor: '#f0f6ff', borderColor: '#2662eb' } : {}}
                      data-testid={`event-tipo-${t.tipo_evento_id}`}>
                      <CalendarCheck className={`w-3.5 h-3.5 ${active ? 'text-[#2662eb]' : 'text-slate-400'}`} />
                      <span className={`text-[11px] font-semibold leading-tight ${active ? 'text-[#2662eb]' : 'text-slate-700'}`}>{t.nome}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedEventTipo?.permite_pessoa && (
              <div className="relative mt-2" data-testid="event-pessoa-section">
                <div className="flex items-center gap-1.5 mb-2">
                  <Label className="text-sm font-semibold text-slate-800">Pessoas associadas ao evento</Label>
                  <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">Associe uma ou mais pessoas a este evento. Este campo não é obrigatório.</p></TooltipContent></Tooltip></TooltipProvider>
                </div>
                {eventForm.pessoa_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="event-pessoas-tags">
                    {eventForm.pessoa_ids.map((pid, idx) => (
                      <div key={pid} className="flex items-center gap-1.5 px-2.5 py-1 border border-blue-200 bg-blue-50 rounded-full" data-testid={`event-pessoa-tag-${idx}`}>
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-[8px] flex-shrink-0">
                          {eventForm.pessoas_nomes[idx]?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <span className="text-xs font-medium text-slate-800 max-w-[120px] truncate">{eventForm.pessoas_nomes[idx]}</span>
                        <button type="button" className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`event-remove-pessoa-${idx}`}
                          onClick={() => {
                            const ids = [...eventForm.pessoa_ids]; const nomes = [...eventForm.pessoas_nomes];
                            ids.splice(idx, 1); nomes.splice(idx, 1);
                            setEventForm({ ...eventForm, pessoa_ids: ids, pessoas_nomes: nomes, pessoa_id: ids[0] || '', pessoa_nome: nomes[0] || '' });
                          }}><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={eventPessoaSearch} onChange={(e) => { setEventPessoaSearch(e.target.value); setEventPessoaDropdown(true); }} onFocus={() => setEventPessoaDropdown(true)} className="pl-8 h-9 text-sm" placeholder="Buscar pessoa..." data-testid="event-pessoa-search" />
                  {eventPessoaDropdown && eventPessoaSearch.length >= 2 && (() => {
                    const selectedIds = new Set(eventForm.pessoa_ids || []);
                    const results = pessoas.filter(p => !selectedIds.has(p.pessoa_id) && norm(p.nome_completo).includes(norm(eventPessoaSearch))).slice(0, 8);
                    if (results.length === 0) return null;
                    return (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {results.map(p => (
                          <button key={p.pessoa_id} type="button" className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm border-b border-slate-50 last:border-b-0"
                            data-testid={`event-pessoa-option-${p.pessoa_id}`}
                            onClick={() => {
                              const ids = [...(eventForm.pessoa_ids || []), p.pessoa_id];
                              const nomes = [...(eventForm.pessoas_nomes || []), p.nome_completo];
                              setEventForm({ ...eventForm, pessoa_ids: ids, pessoas_nomes: nomes, pessoa_id: ids[0] || '', pessoa_nome: nomes[0] || '' });
                              setEventPessoaSearch(''); setEventPessoaDropdown(false);
                            }}>
                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[10px] flex-shrink-0">
                              {p.nome_completo?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                            </div>
                            <span className="truncate">{p.nome_completo}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div><Label className="text-sm">Nome do evento *</Label><Input value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} maxLength={20} className="mt-1" data-testid="event-name-input" /></div>
            <div><Label className="text-sm">Data e Hora *</Label><Input type="datetime-local" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="mt-1" data-testid="event-date-input" /></div>
            <div className="flex items-center justify-between px-3 py-3 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Evento Público</p>
                  <p className="text-[10px] text-slate-400">Visível na página de Agenda</p>
                </div>
              </div>
              <Switch checked={eventForm.publico} onCheckedChange={(checked) => { if (checked) setPublicoConfirm(true); else setEventForm({ ...eventForm, publico: false }); }} data-testid="toggle-publico-visita" />
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-800 mb-2 block">Visibilidade do evento</Label>
              <div className="grid grid-cols-3 gap-2">
                {VISIBILIDADE_OPTIONS.map(o => {
                  const Icon = o.icon;
                  const active = eventForm.visibilidade === o.value;
                  const disabled = o.value === 'publico';
                  return (
                    <button key={o.value} type="button" onClick={() => !disabled && setEventForm({ ...eventForm, visibilidade: o.value })}
                      className={`py-2.5 px-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 border-slate-200 bg-white hover:border-slate-300 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      style={active && !disabled ? { backgroundColor: '#f0f6ff', borderColor: '#2662eb' } : {}}
                      disabled={disabled}
                      data-testid={`event-vis-${o.value}`}>
                      <Icon className={`w-4 h-4 ${active && !disabled ? 'text-[#2662eb]' : 'text-slate-400'}`} />
                      <span className={`text-[11px] font-semibold leading-tight text-center ${active && !disabled ? 'text-[#2662eb]' : 'text-slate-700'}`}>{o.label}</span>
                      <span className="text-[9px] text-slate-400 leading-tight text-center">{o.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between">
              {editingEventId ? (
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:bg-red-50 hover:text-red-600" disabled={eventSaving} data-testid="delete-agendamento-btn"
                    onClick={async () => {
                      try {
                        setEventSaving(true);
                        await fetch(`${API}/events/by-visita/${eventForm.visita_id}`, { method: 'DELETE', credentials: 'include' });
                        await fetch(`${API}/visitas/${eventForm.visita_id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ status: 'pendente' }) });
                        toast.success('Agendamento excluído');
                        setEventOpen(false);
                        onRefresh();
                      } catch { toast.error('Erro ao excluir agendamento'); }
                      finally { setEventSaving(false); }
                    }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger><TooltipContent><p>Excluir agendamento e reverter para Pendente</p></TooltipContent></Tooltip></TooltipProvider>
              ) : <div />}
              <Button onClick={handleCreateEvent} className="bg-blue-600 hover:bg-blue-700" disabled={eventSaving} data-testid="submit-event-btn">
                {eventSaving ? 'Salvando...' : editingEventId ? 'Salvar' : 'Criar Evento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={publicoConfirm} onOpenChange={setPublicoConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tornar evento público?</AlertDialogTitle>
            <AlertDialogDescription>Este evento será visível na página pública de Agenda da igreja. Qualquer pessoa com o link poderá ver os detalhes do evento.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setEventForm(prev => ({ ...prev, publico: true })); setPublicoConfirm(false); }} style={{ backgroundColor: '#2662eb' }}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
